import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import styled from 'styled-components'

import { useAppDispatch } from 'hooks/reduxHooks'
import { rem } from 'polished'

import {
  getCheapestPurchasableOption,
  getIsTourOfferPurchasable,
  getSortedItinerary,
} from 'selectors/TourV2/tourV2OfferPageSelectors'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getShareModalConfig, ShareModalConfig } from 'lib/customer/getShareModalConfig'
import {
  TOUR_V2_CONTENT_SECTION_LABELS,
  TOUR_V2_STICKY_HEADER_ENABLED_SECTIONS,
  TourV2ContentSections,
} from 'constants/tours'
import { tourV2RoomSelectionInitialState } from 'contexts/TourV2/RoomSelection/tourV2RoomSelectionState'
import delay from 'lib/utils/delay'
import determineTourV2OfferContentSections from 'lib/tours/determineTourV2OfferContentSections'
import generateTourV2CheckoutItems from 'lib/tours/generateTourV2CheckoutItems'
import Heading from 'components/Luxkit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import tourV2RoomSelectionStateReducer from 'contexts/TourV2/RoomSelection/tourV2RoomSelectionStateReducer'
import useHasGotToAnchor from 'hooks/useHasGotToAnchor'
import useOnScreen from 'hooks/useOnScreen'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

import TourV2Departures from './Departures/TourV2Departures'
import TourV2FinePrint from './FinePrint/TourV2FinePrint'
import TourV2HeadData from './TourV2HeadData'
import TourV2Inclusions from './Inclusions/TourV2Inclusions'
import TourV2ItineraryAndMap from './Itinerary/TourV2ItineraryAndMap'
import TourV2Variations from './TourVariations/TourV2Variations'
import TourV2Overview from './TourV2Overview'
import TourV2StickyHeader from './StickyElements/TourV2StickyHeader'
import TourV2FAQ from './FAQ/TourV2FAQ'
import TourV2BookWithConfidence from './BookWithConfidence/TourV2BookWithConfidence'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import TourV2UnavailabilityCard from './TourV2UnavailabilityCard'
import TextButton from 'components/Luxkit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { addGTMEvent, fireEcommerceGTMEvent } from 'api/googleTagManager'
import { addToCartGTM, addTourToCart } from 'analytics/eventDefinitions'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import { isUltraLuxTour } from 'lib/offer/offerUtils'
import TourV2YouMayAlsoLikeCarrousel from './YouMayAlsoLike/TourV2YouMayAlsoLikeCarrousel'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import GeoContext from 'contexts/geoContext'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import * as Analytics from 'analytics/analytics'
import generatePartialTourV2CheckoutItems from 'lib/tours/generatePartialTourV2CheckoutItems'
import useTourV2Details from 'hooks/TourV2/useTourV2Details'
import OfferPageGallery from '../OfferPageGallery'
import { addToCart, offerPageHotLeadEvent } from 'analytics/snowplow/events'
import TourV2StickyFooter from './StickyElements/TourV2StickyFooter'
import usePrevious from 'hooks/usePrevious'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import Group from 'components/utils/Group'

const UnavailabilitySectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(40)};
  padding-top: ${rem(40)};
`

const SectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(40)};
  padding-top: ${rem(40)};

  > .section-title + .section-subtitle {
    margin-top: ${rem(8)}
  }

  > .section-title + .section-content,
  > .section-subtitle + .section-content {
    margin-top: ${rem(32)};
  }
`

const OverviewSectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(24)};
  padding-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-bottom: ${rem(64)};
  }
`

interface MappedProps {
  cheapestPurchasableOption: Tours.V2OfferPurchasableOption
  isOfferPurchasable: boolean
  itinerary: Array<Tours.V2OfferItineraryItem>
  offer: Tours.V2Offer
  rooms?: OfferPageState['rooms']
  selectedDepartureMonthIndex?: App.MonthIndex
  selectedDepartureYear?: number
  selectedVariation?: Tours.V2OfferVariation
}

interface Props {
  shareModalConfig?: ShareModalConfig
  currency?: string
}

interface SectionAnchor {
  end?: HTMLDivElement
  start?: HTMLDivElement
}

function TourV2Content(props: Props & MappedProps) {
  const {
    cheapestPurchasableOption,
    isOfferPurchasable,
    itinerary,
    offer,
    rooms,
    selectedVariation,
    selectedDepartureMonthIndex,
    selectedDepartureYear,
    shareModalConfig,
    currency,
  } = props

  const { finePrint, faq } = offer

  const [activeSection, setActiveSection] = useState<TourV2ContentSections>()
  const [scrollTarget, setScrollTarget] = useState<TourV2ContentSections>(null)

  const galleryAnchor = useRef<SectionAnchor>({ start: undefined, end: undefined })
  const overviewAnchor = useRef<SectionAnchor>({ start: undefined, end: undefined })
  const variationsAnchor = useRef<SectionAnchor>({ start: undefined, end: undefined })
  const itineraryAnchor = useRef<SectionAnchor>({ start: undefined, end: undefined })
  const inclusionsAnchor = useRef<SectionAnchor>({ start: undefined, end: undefined })
  const departuresDatesAnchor = useRef<SectionAnchor>({ start: undefined, end: undefined })
  const finePrintAnchor = useRef<SectionAnchor>({ start: undefined, end: undefined })
  const faqAnchor = useRef<SectionAnchor>({ start: undefined, end: undefined })
  const footerAnchor = useRef<SectionAnchor>({ start: undefined, end: undefined })

  const dispatch = useAppDispatch()

  const sectionsVisible: Record<TourV2ContentSections, boolean> = {
    [TourV2ContentSections.GALLERY]: useOnScreen(galleryAnchor.current?.start, galleryAnchor.current?.end, -120, 120),
    [TourV2ContentSections.OVERVIEW]: useOnScreen(overviewAnchor.current?.start, overviewAnchor.current?.end, -120, 120),
    [TourV2ContentSections.VARIATIONS]: useOnScreen(variationsAnchor.current?.start, variationsAnchor.current?.end, -120, 120),
    [TourV2ContentSections.ITINERARY]: useOnScreen(itineraryAnchor.current?.start, itineraryAnchor.current?.end, -120, 120),
    [TourV2ContentSections.DEPARTURES]: useOnScreen(departuresDatesAnchor.current?.start, departuresDatesAnchor.current?.end, -120, 120),
    [TourV2ContentSections.INCLUSIONS]: useOnScreen(inclusionsAnchor.current?.start, inclusionsAnchor.current?.end, -120, 120),
    [TourV2ContentSections.FINE_PRINT]: useOnScreen(finePrintAnchor.current?.start, finePrintAnchor.current?.end, -120, 120),
    [TourV2ContentSections.FAQ]: useOnScreen(faqAnchor.current?.start, faqAnchor.current?.end),
    [TourV2ContentSections.YOU_MAY_ALSO_LIKE]: null,
  }

  const hasGotToOverview = useHasGotToAnchor(overviewAnchor.current?.start, -110)
  const hasGotToDepartures = useHasGotToAnchor(departuresDatesAnchor.current?.start, -136)

  const scrollToAnchor = useCallback(
    (section: TourV2ContentSections) => {
      const sectionAnchorsRefs: Record<TourV2ContentSections, React.MutableRefObject<SectionAnchor>> = {
        [TourV2ContentSections.GALLERY]: galleryAnchor,
        [TourV2ContentSections.OVERVIEW]: overviewAnchor,
        [TourV2ContentSections.VARIATIONS]: variationsAnchor,
        [TourV2ContentSections.ITINERARY]: itineraryAnchor,
        [TourV2ContentSections.DEPARTURES]: departuresDatesAnchor,
        [TourV2ContentSections.INCLUSIONS]: inclusionsAnchor,
        [TourV2ContentSections.FINE_PRINT]: finePrintAnchor,
        [TourV2ContentSections.FAQ]: faqAnchor,
        [TourV2ContentSections.YOU_MAY_ALSO_LIKE]: null,
      }

      setScrollTarget(section)
      sectionAnchorsRefs[section].current?.start.scrollIntoView()
    },
    [setScrollTarget],
  )

  const contentSections = useMemo(() =>
    determineTourV2OfferContentSections(offer, selectedVariation)
  , [offer, selectedVariation])

  const headerContentSections = useMemo(() => [...contentSections].filter(section => TOUR_V2_STICKY_HEADER_ENABLED_SECTIONS.has(section)),
    [contentSections])

  const [roomSelectionState, roomSelectionStateDispatch] = useReducer(
    tourV2RoomSelectionStateReducer,
    tourV2RoomSelectionInitialState,
  )

  const proceedToCheckout = useCallback((
    startDate: string,
    endDate: string,
    occupantsPurchasableOptions: Map<App.Occupants, Tours.V2OfferPurchasableOption>,
    priceTotal: number,
  ) => {
    dispatch(addToCheckoutCart(
      generateTourV2CheckoutItems(
        offer,
        startDate,
        endDate,
        selectedVariation?.images[0],
        occupantsPurchasableOptions,
      ),
    ))
    const convertOccupancyToArray = [...occupantsPurchasableOptions.keys()]
    fireEcommerceGTMEvent(addToCartGTM(offer, currency, priceTotal, sumUpOccupancies(convertOccupancyToArray), 'tour'))
    // Temporary send event GA UA
    addGTMEvent(addTourToCart(offer, currency, priceTotal, sumUpOccupancies(convertOccupancyToArray)))
    dispatch(navigateToCheckout())
    Analytics.trackEvent(addToCart())

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer, selectedVariation])

  const proceedToPackageUpgrade = useCallback((
    selectedDeparture: Tours.V2OfferDeparture,
    selectableOccupancies: Array<App.Occupants>,
  ) => {
    dispatch(addToCheckoutCart(
      generatePartialTourV2CheckoutItems(
        offer,
        selectedDeparture,
        selectedVariation?.images[0],
        selectableOccupancies,
      ),
    ))
    dispatch(navigateToCheckout())
  }, [dispatch, offer, selectedVariation?.images])

  const currentOnScreenSection = Object.keys(sectionsVisible).find((section) => sectionsVisible[section] && (!scrollTarget || section === scrollTarget)) as TourV2ContentSections

  const acknowledgeVariarionChange = useCallback((variationId: string) => {
    const viewingVariation = offer.variations[variationId]
    delay(500).then(() => showSnackbar(
      `You're now viewing ${viewingVariation.name}`,
      'success',
    ))
  }, [offer])

  useEffect(
    () => {
      const hasScrolledToTargetSection = !scrollTarget || currentOnScreenSection === scrollTarget
      if (hasScrolledToTargetSection) {
        setActiveSection(currentOnScreenSection)
        setScrollTarget(null)
      }
    },
    [scrollTarget, currentOnScreenSection],
  )

  const geoState = useContext(GeoContext)

  const isUltraLux = isUltraLuxTour(offer)
  let paletteValue: ProductPalette
  if (isUltraLux) {
    paletteValue = 'ultralux'
  } else {
    paletteValue = 'default'
  }

  const { hasAlternativeRoute } = useTourV2Details(offer)

  const previousSelectedDepartureMonthIndex = usePrevious(selectedDepartureMonthIndex)
  const hotLead = useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    occupancy: rooms,
    variant: Object.keys(offer.variations).length > 1 ? selectedVariation?.fkTourOptionId : undefined,
    year: selectedDepartureYear,
    month: ((selectedDepartureMonthIndex ?? 0) + 1),
  })
  useEffect(() => {
    if (previousSelectedDepartureMonthIndex != undefined && hotLead) {
      Analytics.trackEvent(offerPageHotLeadEvent(offer, hotLead, geoState))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  const alternativeVariation = useMemo(() => {
    if (hasAlternativeRoute) {
      const alternativeVariationId = Object.keys(offer.variations).filter((variationId) => variationId !== selectedVariation?.id)[0]
      return offer.variations[alternativeVariationId]
    }
  }, [selectedVariation, hasAlternativeRoute, offer.variations])

  // we only recommend tours with time filter when user specifically select a departure time rather than using the default departure time
  const defaultDeparture = useRef('')
  const [recommendDepartureYear, recommendDepartureMonth] = useMemo(() => {
    if (selectedDepartureYear && selectedDepartureMonthIndex !== undefined) {
      const selectedDeparture = `${selectedDepartureYear}-${selectedDepartureMonthIndex}`
      if (!defaultDeparture.current) {
        defaultDeparture.current = selectedDeparture
      }
      if (selectedDeparture !== defaultDeparture.current) {
        return [selectedDepartureYear, selectedDepartureMonthIndex]
      }
    }
    return [undefined, undefined]
  }, [selectedDepartureYear, selectedDepartureMonthIndex])

  const shareConfig = shareModalConfig ? getShareModalConfig(shareModalConfig) : undefined

  return (<ProductPaletteProvider palette={isUltraLux ? 'ultralux' : 'default'}>

    <TourV2HeadData offer={offer} variation={selectedVariation} />

    <TourV2StickyHeader
      contentSections={headerContentSections}
      onScrollTo={scrollToAnchor}
      purchasableOption={cheapestPurchasableOption}
      visible={hasGotToOverview}
      title={selectedVariation?.name}
      activeSection={activeSection}
      buttonPaletteValue={paletteValue}
    />

    <div id="TOUR_V2_CONTENT_TOP_ANCHOR" aria-hidden="true" />

    {!isOfferPurchasable && !!selectedVariation && <Pane>
      <UnavailabilitySectionContainer>
        <Group direction="vertical" gap={40}>
          <TourV2UnavailabilityCard offer={offer} offerVariation={selectedVariation} />
          <Group direction="vertical" gap={8} horizontalAlign="center">
            <Heading variant="heading3" colour="neutral-one">More offers available</Heading>
            <TextButton kind="secondary" to="/tours">Browse all tour offers</TextButton>
          </Group>
        </Group>
      </UnavailabilitySectionContainer>
    </Pane>}

    {contentSections.has(TourV2ContentSections.GALLERY) && <>
      <ScrollAnchor ref={node => { galleryAnchor.current.start = node }} />
      <OfferPageGallery
        offer={offer}
        tourVariation={selectedVariation}
        shareModalConfig={shareConfig}
      />
      <ScrollAnchor ref={node => { galleryAnchor.current.end = node }} />
    </>}
    <ProductPaletteProvider palette={paletteValue}>
      <LuxPlusLabelProvider page="offer-page">
        <Pane>
          <OverviewSectionContainer>
            <ScrollAnchor ref={node => { overviewAnchor.current.start = node }} />
            <TourV2Overview
            cheapestPurchasableOption={cheapestPurchasableOption}
            shareModalConfig={shareModalConfig}
            onView={scrollToAnchor}
            buttonPaletteValue={paletteValue}
          />
            <ScrollAnchor ref={node => { overviewAnchor.current.end = node }} />
          </OverviewSectionContainer>

          {contentSections.has(TourV2ContentSections.VARIATIONS) && <SectionContainer size="lg">
            <ScrollAnchor ref={node => { variationsAnchor.current.start = node }} />
            <Heading variant="heading3" align="start" className="section-title">Choose a tour option</Heading>
            <div className="section-content">
              <TourV2Variations onVariationChanged={acknowledgeVariarionChange} />
            </div>
            <ScrollAnchor ref={node => { variationsAnchor.current.end = node }} id="TOUR_V2_ITINERARY_TOP_ANCHOR"/>
          </SectionContainer>}
          {contentSections.has(TourV2ContentSections.ITINERARY) && <SectionContainer
          size={!selectedVariation?.routeMapImage ? 'md' : undefined}
        >
            <ScrollAnchor ref={node => { itineraryAnchor.current.start = node }} />
            <Heading variant="heading2" align="center" className="section-title">
              {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.ITINERARY]}{selectedVariation?.routeMapImage && ' & map'}
            </Heading>
            <div className="section-content">
              <TourV2ItineraryAndMap
              itinerary={itinerary}
              variation={selectedVariation}
              offerType={offer.type}
              alternativeVariation={alternativeVariation}
              onAlternativeRouteChange={acknowledgeVariarionChange}
            />
            </div>
            <ScrollAnchor ref={node => { itineraryAnchor.current.end = node }} />
          </SectionContainer>}

          {contentSections.has(TourV2ContentSections.INCLUSIONS) && <SectionContainer size="md">
            <ScrollAnchor ref={node => { inclusionsAnchor.current.start = node }} />
            <Heading variant="heading2" align="center" className="section-title">
              {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.INCLUSIONS]}
            </Heading>
            <Subtitle variant="subtitle2" align="center" className="section-subtitle">
              The following are included in your tour package:
            </Subtitle>
            <div className="section-content">
              <TourV2Inclusions
              diningInclusions={selectedVariation?.diningInclusions}
              travelInclusions={selectedVariation?.travelInclusions}
            />
            </div>
            <ScrollAnchor ref={node => { inclusionsAnchor.current.end = node }} />
          </SectionContainer>}
        </Pane>

        <Pane type="light">
          <SectionContainer size="lg">
            <ScrollAnchor ref={node => { departuresDatesAnchor.current.start = node }} />
            <Heading variant="heading2" align="center" className="section-title">
              {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.DEPARTURES]}
            </Heading>
            <div className="section-content">
              <TourV2Departures
              roomSelectionState={roomSelectionState}
              roomSelectionStateDispatch={roomSelectionStateDispatch}
              onProceedToCheckout={proceedToCheckout}
              onProceedToPackageUpgrade={proceedToPackageUpgrade}
            />
            </div>
            <ScrollAnchor ref={node => { departuresDatesAnchor.current.end = node }} />
          </SectionContainer>
        </Pane>
      </LuxPlusLabelProvider>

      <Pane>
        <SectionContainer size="md">
          <ScrollAnchor ref={node => { finePrintAnchor.current.start = node }} />
          <Heading variant="heading2" align="center" className="section-title">
            {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.FINE_PRINT]}
          </Heading>
          <div className="section-content">
            <TourV2FinePrint finePrint={finePrint} />
          </div>
          <ScrollAnchor ref={node => { finePrintAnchor.current.end = node }} />
        </SectionContainer>

        {contentSections.has(TourV2ContentSections.FAQ) && <SectionContainer size="md">
          <ScrollAnchor ref={node => { faqAnchor.current.start = node }} />
          <Heading variant="heading2" align="center" className="section-title">
            {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.FAQ]}
          </Heading>
          <div className="section-content">
            <TourV2FAQ faq={faq} />
          </div>
          <ScrollAnchor ref={node => { faqAnchor.current.end = node }} />
        </SectionContainer>}

        {contentSections.has(TourV2ContentSections.YOU_MAY_ALSO_LIKE) && <SectionContainer as="div" >
          <div className="section-content">
            <TourV2YouMayAlsoLikeCarrousel
              offerId={offer.id}
              trackingDescription="Tours"
              departureYear={recommendDepartureYear}
              departureMonth={recommendDepartureMonth}
            />
          </div>
        </SectionContainer>}

      </Pane>
      <ScrollAnchor ref={node => { footerAnchor.current.start = node }} />
      {isOfferPurchasable && <TourV2StickyFooter
        visible={!hasGotToDepartures}
        purchasableOption={cheapestPurchasableOption}
        onScrollTo={scrollToAnchor}
      />}

      {contentSections.has(TourV2ContentSections.FAQ) && <SectionContainer size="md">
        <div className="section-content">
          <TourV2BookWithConfidence />
        </div>
      </SectionContainer>}
    </ProductPaletteProvider>
  </ProductPaletteProvider>)
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState, appState: App.State): MappedProps => {
  return {
    cheapestPurchasableOption: getCheapestPurchasableOption(offerPageState, appState),
    itinerary: getSortedItinerary(offerPageState),
    offer: offerPageState.tourV2Offer,
    rooms: offerPageState.rooms,
    selectedDepartureMonthIndex: offerPageState.selectedDepartureMonthIndex,
    selectedDepartureYear: offerPageState.selectedDepartureYear,
    selectedVariation: offerPageState.selectedTourV2OfferVariation,
    isOfferPurchasable: getIsTourOfferPurchasable(offerPageState),
  }
}

export default withOfferPageAndReduxState(mapOfferPageStateToProps)(TourV2Content)
